class UBBannerSlides extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.viewport = this.querySelector('[data-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.previousButton = this.querySelector('[data-previous]');
    this.nextButton = this.querySelector('[data-next]');
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.status = this.querySelector('[data-status]');
    this.autoplayButton = this.querySelector('[data-autoplay-toggle]');
    this.activeIndex = 0;

    if (!this.viewport || this.slides.length < 2) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.interval = Math.min(20, Math.max(3, Number(this.dataset.autoplayInterval) || 6)) * 1000;
    this.userPaused = this.reducedMotion.matches;
    this.motionOverride = false;
    this.hovered = false;
    this.focusWithin = false;
    this.pointerDown = false;
    this.editorSelected = false;
    this.inView = !('IntersectionObserver' in window);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    const listenerOptions = { signal: this.abortController.signal };
    this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1), listenerOptions);
    this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1), listenerOptions);
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index), listenerOptions));
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true, signal: this.abortController.signal });
    this.viewport.addEventListener('keydown', this.onKeydown, listenerOptions);

    if (this.autoplayEnabled) {
      this.autoplayButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (!this.userPaused) this.motionOverride = true;
        this.updateAutoplayButton();
        this.scheduleAutoplay();
      }, listenerOptions);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.hovered = true;
        this.scheduleAutoplay();
      }, listenerOptions);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.scheduleAutoplay();
      }, listenerOptions);
      this.addEventListener('focusin', () => {
        this.focusWithin = true;
        this.scheduleAutoplay();
      }, listenerOptions);
      this.addEventListener('focusout', (event) => {
        this.focusWithin = this.contains(event.relatedTarget);
        this.scheduleAutoplay();
      }, listenerOptions);
      this.viewport.addEventListener('pointerdown', () => {
        this.pointerDown = true;
        this.scheduleAutoplay();
      }, listenerOptions);
      const releasePointer = () => {
        if (!this.pointerDown) return;
        this.pointerDown = false;
        this.scheduleAutoplay();
      };
      window.addEventListener('pointerup', releasePointer, listenerOptions);
      window.addEventListener('pointercancel', releasePointer, listenerOptions);
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), listenerOptions);
      this.reducedMotion.addEventListener('change', () => {
        this.motionOverride = false;
        if (this.reducedMotion.matches) this.userPaused = true;
        this.updateAutoplayButton();
        this.scheduleAutoplay();
      }, listenerOptions);
      document.addEventListener('shopify:block:select', (event) => {
        if (!event.target.contains(this)) return;
        this.editorSelected = true;
        this.scheduleAutoplay();
      }, listenerOptions);
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!event.target.contains(this)) return;
        this.editorSelected = false;
        this.scheduleAutoplay();
      }, listenerOptions);
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
          this.scheduleAutoplay();
        }, { threshold: [0, 0.25] });
        this.visibilityObserver.observe(this);
      }
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateFromScroll());
      this.resizeObserver.observe(this.viewport);
    }

    this.update(0);
    this.updateAutoplayButton();
    this.scheduleAutoplay();
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.stopAutoplay();
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    window.cancelAnimationFrame(this.scrollFrame);
    this.initialized = false;
  }

  canAutoplay() {
    return this.initialized && this.autoplayEnabled && !this.userPaused &&
      (!this.reducedMotion.matches || this.motionOverride) && this.inView &&
      !document.hidden && !this.hovered && !this.focusWithin &&
      !this.pointerDown && !this.editorSelected;
  }

  stopAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  scheduleAutoplay() {
    this.stopAutoplay();
    // Avoid announcing every automatic slide to screen readers.
    this.status?.setAttribute('aria-live', this.canAutoplay() ? 'off' : 'polite');
    if (!this.canAutoplay()) return;
    this.autoplayTimer = window.setTimeout(() => {
      if (!this.canAutoplay()) return;
      this.goTo((this.activeIndex + 1) % this.slides.length);
    }, this.interval);
  }

  updateAutoplayButton() {
    if (!this.autoplayButton) return;
    const paused = this.userPaused;
    const label = paused ? this.autoplayButton.dataset.playLabel : this.autoplayButton.dataset.pauseLabel;
    this.autoplayButton.dataset.paused = String(paused);
    this.autoplayButton.setAttribute('aria-label', label);
    this.autoplayButton.setAttribute('title', label);
  }

  onScroll() {
    this.stopAutoplay();
    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.updateFromScroll();
      this.scheduleAutoplay();
    });
  }

  onKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
  }

  goTo(index) {
    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const target = this.slides[nextIndex];
    if (!target) return;

    this.viewport.scrollTo({
      left: target.offsetLeft,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
    this.update(nextIndex);
    this.scheduleAutoplay();
  }

  updateFromScroll() {
    const viewportLeft = this.viewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - viewportLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.update(closestIndex);
  }

  update(index) {
    this.activeIndex = index;
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.inert = !isActive;
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    if (this.previousButton) this.previousButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (this.status) this.status.textContent = `${index + 1} / ${this.slides.length}`;
  }
}

if (!customElements.get('ub-banner-slides')) {
  customElements.define('ub-banner-slides', UBBannerSlides);
}
